import { Alert, Box, Grid, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { BlockStatus, BlockStatusPayload, getBlockStatus, getSoftBlockStatus, updateBlockStatus, updateSoftBlockStatus } from "src/apis/upsell";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { Id } from "src/utils/types";
import BlockStatusForm from "./BlockStatusForm";

type BlockSettingsTabProps = {
  companyId: Id;
};

const BlockSettingsTab: FC<BlockSettingsTabProps> = ({ companyId }) => {
  const [blockStatus, setBlockStatus] = useState<BlockStatus | null>(null);
  const [softBlockStatus, setSoftBlockStatus] = useState<BlockStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError("");
    try {
      const [fullBlock, softBlock] = await Promise.all([
        getBlockStatus(companyId),
        getSoftBlockStatus(companyId),
      ]);
      setBlockStatus(fullBlock);
      setSoftBlockStatus(softBlock);
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar bloqueios");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveBlock = async (payload: BlockStatusPayload) => {
    if (!companyId) return;
    await updateBlockStatus(companyId, payload);
    fetchData();
  };

  const handleSaveSoftBlock = async (payload: BlockStatusPayload) => {
    if (!companyId) return;
    await updateSoftBlockStatus(companyId, payload);
    fetchData();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {isLoading ? (
        <SkeletonLoad variant="cards" rows={2} />
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <BlockStatusForm
              title="Bloqueio da conta (Full Block)"
              description="Suspende completamente ações administrativas da empresa. Utilize em casos de inadimplência geral."
              status={blockStatus}
              onSave={handleSaveBlock}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <BlockStatusForm
              title="SoftBlock - Sessões extras"
              description="Bloqueia apenas as sessões contratadas pelo RH no plano da empresa, mantendo liberados os agendamentos extras pagos diretamente pelos colaboradores."
              status={softBlockStatus}
              onSave={handleSaveSoftBlock}
            />
          </Grid>
        </Grid>
      )}
      <Typography variant="body2" color="text.secondary">
        Dica: descreva nas mensagens o motivo do bloqueio e o contato para regularização. Os psicólogos verão o aviso quando tentarem cadastrar novos atendimentos.
      </Typography>
    </Box>
  );
};

export default BlockSettingsTab;
